"use client"

import { Bot, Shield } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { TradeHistoryStats } from "@/lib/agent-force"
import { ScrollArea } from "@/components/ui/scroll-area"

function fmtMoney(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v)
  if (!Number.isFinite(n)) return "—"
  const out = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n)
  return n > 0 ? `+${out}` : out
}

function pairFromSymbol(symbol: string): string {
  const s = String(symbol || "").replace(/[^A-Za-z]/g, "").toUpperCase()
  if (s.length === 6) return `${s.slice(0, 3)}/${s.slice(3)}`
  return symbol || "—"
}

type TradeRow = {
  id: string
  source: "position" | "history" | "mt5_deal"
  pair: string
  side: "BUY" | "SELL"
  status: "open" | "closed" | "pending" | "failed" | "other" | "deal"
  lot: number
  unitsText: string
  entryPrice: number | null
  currentOrExitPrice: number | null
  entryValueText: string
  currentValueText: string
  openedAt: string
  closedAt: string | null
  pnl: string
  result: "win" | "loss" | "flat" | "open" | "pending"
  closeReason?: string
  exitTriggerType?: string | null
  isAgentDeal: boolean
  dealLeg?: string | null
  dealTicket?: number | null
  positionId?: number | null
  notionalHint?: string | null
  approxNotional?: number | null
}

type Mt5DealGroup = {
  key: string
  positionId: number | null
  latestIn: Record<string, unknown> | null
  latestOut: Record<string, unknown> | null
  openedAtMs: number
  openedAtText: string
  closedAtText: string | null
  pnlTotal: number
  isAgentDeal: boolean
  approxNotional: number | null
  notionalHint: string | null
  dealTicket: number | null
}

function toNum(v: unknown): number | null {
  const n = typeof v === "number" ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function parseTimeMs(iso: unknown): number {
  const t = new Date(String(iso ?? "")).getTime()
  return Number.isFinite(t) ? t : 0
}

function unitsFromLot(lot: number): number {
  // FX standard lot heuristic for dashboard display only.
  return lot * 100_000
}

function fmtNumber(n: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n)
}

function priceDecimalsFromPair(pair: string): number {
  return pair.toUpperCase().includes("JPY") ? 2 : 5
}

function fmtPrice(pair: string, v: number | null): string {
  if (v == null) return "—"
  return v.toFixed(priceDecimalsFromPair(pair))
}

function quoteCurrencyFromPair(pair: string): string | null {
  const p = pair.toUpperCase()
  if (p.includes("JPY")) return "JPY"
  if (p.includes("XAU") || p.includes("GOLD")) return "XAU"
  return null
}

/** Notional at fill — quote currency for JPY crosses, USD-style for majors when unknown. */
function fmtExposureValue(
  pair: string,
  units: number,
  price: number | null,
  approxNotional?: number | null,
): string {
  const q = quoteCurrencyFromPair(pair)
  const raw =
    approxNotional != null && Number.isFinite(approxNotional)
      ? approxNotional
      : price == null
        ? null
        : units * price
  if (raw == null) return "—"
  if (q) return `${fmtNumber(raw)} ${q} (approx.)`
  return fmtMoney(raw)
}

function normalizeStatus(raw: unknown): TradeRow["status"] {
  const s = String(raw ?? "").toLowerCase()
  if (s === "open") return "open"
  if (s === "closed") return "closed"
  if (s === "pending") return "pending"
  if (s === "failed_open") return "failed"
  return "other"
}

function exitTriggerLabel(reason: string | null | undefined): string | null {
  const key = String(reason ?? "").trim().toLowerCase()
  if (!key) return null
  const map: Record<string, string> = {
    hard_take_profit: "Hard take-profit",
    hard_stop_loss: "Hard stop-loss",
    time_stop: "Time stop",
    strong_reversal: "Strong reversal",
    major_news_risk: "Major news risk",
    llm_exit_management: "Discretionary exit policy",
    admin_manual_close: "Manual admin close",
    mt5_history_sync: "Broker history sync",
  }
  return map[key] ?? key.replaceAll("_", " ")
}

function toRows(items: Array<Record<string, unknown>>): TradeRow[] {
  const rows: TradeRow[] = []
  const dealGroups = new Map<string, Mt5DealGroup>()

  for (const [idx, x] of items.entries()) {
      const rowType = String(x.row_type ?? "").toLowerCase()
      const isPosition = rowType === "open_position"
      const isMt5Deal = rowType === "mt5_deal"
      if (isMt5Deal) {
        const positionId = toNum(x.position_id)
        const dealTicket = toNum(x.deal_ticket)
        const key =
          positionId != null && positionId > 0
            ? `pos:${positionId}`
            : `deal:${dealTicket ?? idx}`
        const nowMs = parseTimeMs(x.time)
        const leg = String(x.entry_leg ?? "").toLowerCase()
        const existing = dealGroups.get(key)
        const group: Mt5DealGroup = existing ?? {
          key,
          positionId,
          latestIn: null,
          latestOut: null,
          openedAtMs: nowMs,
          openedAtText: x.time ? new Date(String(x.time)).toLocaleString() : "—",
          closedAtText: null,
          pnlTotal: 0,
          isAgentDeal: Boolean(x.is_agent_deal),
          approxNotional: toNum(x.approx_notional),
          notionalHint: x.notional_hint ? String(x.notional_hint) : null,
          dealTicket: dealTicket ?? null,
        }
        if (leg === "in") {
          const prevMs = group.latestIn ? parseTimeMs(group.latestIn.time) : 0
          if (!group.latestIn || nowMs >= prevMs) {
            group.latestIn = x
            group.openedAtMs = nowMs
            group.openedAtText = x.time ? new Date(String(x.time)).toLocaleString() : group.openedAtText
          }
        } else if (leg === "out") {
          const prevMs = group.latestOut ? parseTimeMs(group.latestOut.time) : 0
          if (!group.latestOut || nowMs >= prevMs) {
            group.latestOut = x
            group.closedAtText = x.time ? new Date(String(x.time)).toLocaleString() : null
            group.dealTicket = toNum(x.deal_ticket) ?? group.dealTicket
          }
        }
        group.pnlTotal += toNum(x.pnl_leg) ?? 0
        group.isAgentDeal = group.isAgentDeal || Boolean(x.is_agent_deal)
        if (group.approxNotional == null) group.approxNotional = toNum(x.approx_notional)
        if (!group.notionalHint && x.notional_hint) group.notionalHint = String(x.notional_hint)
        dealGroups.set(key, group)
        continue
      }

      const pair = pairFromSymbol(String(x.symbol ?? ""))
      const sideRaw = String(x.direction ?? "buy")
      const side = sideRaw.toUpperCase() === "SELL" ? "SELL" : "BUY"
      const lot = Math.max(
        0,
        toNum(x.lot) ?? 0,
      )
      const units = unitsFromLot(lot)
      const unitsText = `${fmtNumber(units)} units (${lot.toFixed(2)} lot)`

      const entryPrice = isPosition
        ? toNum(x.price_open)
        : toNum(x.entry_price)
      const currentOrExitPrice = isPosition
        ? toNum(x.price_current)
        : (toNum(x.exit_price) ?? (normalizeStatus(x.status) === "open" ? toNum(x.entry_price) : null))

      const status = isPosition ? "open" : normalizeStatus(x.status)
      const pnlNum = isPosition
        ? toNum(x.profit) ?? 0
        : toNum(x.realized_pnl) ?? 0
      const result: TradeRow["result"] = status === "pending"
        ? "pending"
        : status === "open"
          ? "open"
          : pnlNum > 0
            ? "win"
            : pnlNum < 0
              ? "loss"
              : "flat"

      const opened = x.opened_at
        ? new Date(String(x.opened_at)).toLocaleString()
        : x.created_at
          ? new Date(String(x.created_at)).toLocaleString()
          : "—"
      const closed = x.closed_at ? new Date(String(x.closed_at)).toLocaleString() : null

      const isAgentDeal = Boolean(isPosition ? x.is_agent_deal : true)
      const exitTriggerType = exitTriggerLabel(x.close_reason != null ? String(x.close_reason) : null)

      rows.push({
        id: String(x.id ?? `${isPosition ? "pos" : "hist"}-${idx}`),
        source: isPosition ? "position" : "history",
        pair,
        side,
        status,
        lot,
        unitsText,
        entryPrice,
        currentOrExitPrice,
        entryValueText: fmtExposureValue(pair, units, entryPrice, toNum(x.approx_notional)),
        currentValueText: fmtExposureValue(pair, units, currentOrExitPrice),
        openedAt: opened,
        closedAt: closed,
        pnl: fmtMoney(pnlNum),
        result,
        closeReason: x.close_reason ? String(x.close_reason) : undefined,
        exitTriggerType,
        isAgentDeal,
        dealLeg: null,
        dealTicket: null,
        positionId: toNum(x.mt5_ticket) ?? toNum(x.position_id),
        notionalHint: x.notional_hint ? String(x.notional_hint) : null,
        approxNotional: toNum(x.approx_notional),
      })
    }

  for (const group of dealGroups.values()) {
    const inLeg = group.latestIn
    const outLeg = group.latestOut
    const sourceLeg = inLeg ?? outLeg
    if (!sourceLeg) continue
    const pair = pairFromSymbol(String(sourceLeg.symbol ?? ""))
    const sideRaw = String((inLeg ?? sourceLeg).side ?? "buy")
    const side = sideRaw.toUpperCase() === "SELL" ? "SELL" : "BUY"
    const lot = Math.max(0, toNum((inLeg ?? outLeg)?.volume) ?? 0)
    const units = unitsFromLot(lot)
    const unitsText = `${fmtNumber(units)} units (${lot.toFixed(2)} lot)`
    const entryPrice = toNum(inLeg?.price)
    const currentOrExitPrice = outLeg ? toNum(outLeg.price) : null
    const isClosed = Boolean(outLeg)
    const pnlNum = group.pnlTotal
    const result: TradeRow["result"] = isClosed
      ? pnlNum > 0
        ? "win"
        : pnlNum < 0
          ? "loss"
          : "flat"
      : "open"
    rows.push({
        id: `mt5-pos-${group.key}`,
        source: "mt5_deal",
        pair,
        side,
        status: isClosed ? "deal" : "open",
        lot,
        unitsText,
        entryPrice,
        currentOrExitPrice,
        entryValueText: fmtExposureValue(pair, units, entryPrice, group.approxNotional),
        currentValueText: fmtExposureValue(pair, units, currentOrExitPrice),
        openedAt: group.openedAtText,
        closedAt: group.closedAtText,
        pnl: fmtMoney(pnlNum),
        result,
        closeReason: isClosed ? "mt5_deal_ledger" : undefined,
        exitTriggerType: isClosed ? "Broker round-trip (entry + exit fills)" : "Broker deal in (entry fill)",
        isAgentDeal: group.isAgentDeal,
        dealLeg: isClosed ? "out" : "in",
        dealTicket: group.dealTicket,
        positionId: group.positionId,
        notionalHint: group.notionalHint,
        approxNotional: group.approxNotional,
      })
  }

  rows.sort((a, b) => {
    const at = parseTimeMs(a.closedAt ?? a.openedAt)
    const bt = parseTimeMs(b.closedAt ?? b.openedAt)
    return bt - at
  })
  return rows
}

export function RiskMetrics({
  items,
  stats,
}: {
  items?: Array<Record<string, unknown>>
  stats?: TradeHistoryStats | null
}) {
  const rows = toRows(items ?? [])
  const winRate = stats && stats.closed_total > 0 ? `${(stats.win_rate * 100).toFixed(1)}%` : "—"
  const net = stats ? fmtMoney(stats.net_pnl) : "—"
  const avg = stats ? fmtMoney(stats.avg_pnl) : "—"

  return (
    <Card className="min-w-0 w-full bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Risk Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Historical Trade Results
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-md bg-secondary/30 p-2">
              <p className="text-xs text-muted-foreground">Closed Win Rate</p>
              <p className="font-mono font-semibold">{winRate}</p>
            </div>
            <div className="rounded-md bg-secondary/30 p-2">
              <p className="text-xs text-muted-foreground">Net Realized PnL</p>
              <p className="font-mono font-semibold">{net}</p>
            </div>
            <div className="rounded-md bg-secondary/30 p-2">
              <p className="text-xs text-muted-foreground">Average Trade PnL</p>
              <p className="font-mono font-semibold">{avg}</p>
            </div>
            <div className="rounded-md bg-secondary/30 p-2">
              <p className="text-xs text-muted-foreground">Closed / Open / Pending</p>
              <p className="font-mono font-semibold">
                {stats ? `${stats.closed_total} / ${stats.open_total} / ${stats.pending_total}` : "—"}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            All Trades And Positions
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground/90">Lot</span> is the broker&apos;s size unit (often 1 lot ≈
            100,000 units of the base currency on FX).{" "}
            <span className="font-medium text-foreground/90">Approx. position value</span> uses lots × contract size ×
            price (notional exposure at that fill), not the cash margin you put up.{" "}
            <Bot className="inline h-3.5 w-3.5 align-text-bottom text-primary" aria-hidden /> means this leg matches
            our backend (magic 11001–11003 or a ticket we logged). Plain rows are other platform activity.
          </p>
          <ScrollArea className="h-[360px]">
            <div className="space-y-2 pr-3">
            {rows.length === 0 && (
              <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                No open MT5 positions and no rows in the app trade log. If MetaTrader shows activity, check API
                base URL and that this backend uses the same SQLite and MT5 session as your terminal.
              </div>
            )}
            {rows.map((r) => (
              <div
                key={r.id}
                className="rounded-md bg-secondary/30 p-2.5 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-sm font-semibold flex items-center gap-1.5">
                    {r.isAgentDeal ? (
                      <span className="shrink-0 text-primary" title="Placed by this app’s agents">
                        <Bot className="h-4 w-4" aria-label="Agent trade" />
                      </span>
                    ) : (
                      <span className="inline-block w-4 shrink-0" aria-hidden />
                    )}
                    <span>
                      {r.pair} <span className="text-muted-foreground">{r.side}</span>
                    </span>
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {r.status === "deal" && r.dealLeg
                      ? `deal · ${r.dealLeg}`
                      : r.status}
                  </p>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
                  <p>Size: <span className="font-mono">{r.unitsText}</span></p>
                  <p>Entry: <span className="font-mono">{fmtPrice(r.pair, r.entryPrice)}</span></p>
                  <p>Entry Value: <span className="font-mono">{r.entryValueText}</span></p>
                  <p>
                    {r.status === "closed" || r.status === "deal" ? "Exit / fill" : "Current"}:{" "}
                    <span className="font-mono">{fmtPrice(r.pair, r.currentOrExitPrice)}</span>
                  </p>
                  <p>
                    {r.status === "closed" || r.status === "deal" ? "Exit / fill value" : "Current Value"}:{" "}
                    <span className="font-mono">{r.currentValueText}</span>
                  </p>
                  <p
                    className={`font-mono font-semibold ${
                      r.result === "win"
                        ? "text-positive"
                        : r.result === "loss"
                          ? "text-negative"
                          : "text-muted-foreground"
                    }`}
                  >PnL: {r.pnl}</p>
                </div>
                {r.approxNotional != null && Number.isFinite(r.approxNotional) && (
                  <p className="mt-1 text-muted-foreground">
                    Approx. position value (notional):{" "}
                    <span className="font-mono text-foreground/90">{fmtMoney(r.approxNotional)}</span>
                  </p>
                )}
                {r.notionalHint && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground/90 leading-snug">{r.notionalHint}</p>
                )}
                {r.source === "mt5_deal" && (r.dealTicket != null || r.positionId != null) && (
                  <p className="mt-1 text-[11px] text-muted-foreground font-mono">
                    MT5 deal #{r.dealTicket ?? "—"}
                    {r.positionId != null && r.positionId > 0 ? ` · position ${r.positionId}` : ""}
                  </p>
                )}
                <p className="mt-1 text-muted-foreground">Open: {r.openedAt}</p>
                {r.closedAt && <p className="text-muted-foreground">Close: {r.closedAt}</p>}
                <p className="text-muted-foreground">
                  Result: {r.result}
                  {r.closeReason ? ` · Reason: ${r.closeReason}` : ""}
                </p>
                {r.exitTriggerType && (
                  <p className="text-muted-foreground">Exit trigger: {r.exitTriggerType}</p>
                )}
              </div>
            ))}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  )
}
